import { useCallback, useMemo } from "react";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Stock Alert pending actions, from GET /api/core-list/actions (2026-11-02).
//
// Two kinds, decided server-side so the badge, the tab pills and the list can
// never count differently:
//   - no_live_listing — "Listing not created": nothing live (never listed,
//     sold, or switched off). Core List and Ad List only.
//   - live_auction    — the reference is in a live auction lot, one action
//     per lot. All three lists.
//
// "Mark as done" is per STORE: one person clears it for the whole team, and it
// comes back by itself when the situation changes (a new lot, a listing that
// sells or goes off again) — the backend keys a dismissal on a fingerprint of
// the situation, so nothing here has to track that.

export const LIST_TYPES = ["core", "ad", "pipeline"];

// Every cache entry for one store's actions starts with this, so one
// invalidation refreshes the badge (counts only) and the page (full rows)
// together after a mark-as-done.
export const coreListActionsKey = (storeIdM) => ["core-list-actions", storeIdM];

const EMPTY_COUNTS = { core: 0, ad: 0, pipeline: 0 };
const EMPTY_LISTS = { core: [], ad: [], pipeline: [] };

const fetchActions = async (storeIdM, countsOnly) => {
  const { data } = await axios.get(`/api/core-list/actions`, {
    params: { store_id_m: storeIdM, ...(countsOnly ? { counts_only: 1 } : {}) },
  });
  return data;
};

/**
 * useCoreListActions — the store's open Stock Alert actions.
 *
 * @param {number|null} storeIdM
 * @param {{ enabled?: boolean, countsOnly?: boolean, refetchInterval?: number|false }} [options]
 *   countsOnly: the nav badge's mode — totals only, no rows, a cheaper query.
 * @returns {{
 *   total: number,
 *   counts: { core: number, ad: number, pipeline: number },
 *   lists: { core: object[], ad: object[], pipeline: object[] },
 *   isLoading: boolean,
 *   isError: boolean,
 *   markDone: (action: object) => Promise<void>,
 *   undo: (action: object) => Promise<void>,
 *   refresh: () => Promise<void>,
 * }}
 */
export const useCoreListActions = (
  storeIdM,
  { enabled = true, countsOnly = false, refetchInterval = false } = {}
) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => [...coreListActionsKey(storeIdM), countsOnly ? "counts" : "rows"],
    [storeIdM, countsOnly]
  );

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => fetchActions(storeIdM, countsOnly),
    // No store (useActiveStoreIdM → null) → never ask for ?store_id_m=null.
    enabled: enabled && storeIdM != null,
    refetchInterval,
    staleTime: 30_000,
  });

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: coreListActionsKey(storeIdM) }),
    [queryClient, storeIdM]
  );

  const markDone = useCallback(
    async (action) => {
      await axios.post(`/api/core-list/${action.core_list_item_id}/actions/done`, {
        kind: action.kind,
        fingerprint: action.fingerprint,
      });
      await refresh();
    },
    [refresh]
  );

  const undo = useCallback(
    async (action) => {
      await axios.delete(`/api/core-list/${action.core_list_item_id}/actions/done`, {
        params: { kind: action.kind, fingerprint: action.fingerprint },
      });
      await refresh();
    },
    [refresh]
  );

  return useMemo(
    () => ({
      total: Number(data?.total) || 0,
      counts: { ...EMPTY_COUNTS, ...(data?.counts || {}) },
      lists: { ...EMPTY_LISTS, ...(data?.lists || {}) },
      isLoading,
      isError,
      markDone,
      undo,
      refresh,
    }),
    [data, isLoading, isError, markDone, undo, refresh]
  );
};

export default useCoreListActions;
